import React, { useEffect, useState, useCallback } from 'react';
import { 
  Map, 
  AdvancedMarker, 
  Pin,
  useMap,
  InfoWindow
} from '@vis.gl/react-google-maps';

// Component to render the polyline on the map
const RoutePolyline = ({ path, map }) => {
  useEffect(() => {
    if (!map || !path || path.length < 2) return;

    const polyline = new window.google.maps.Polyline({
      path: path.map(p => ({ lat: p.latitude || p.lat, lng: p.longitude || p.lng })),
      geodesic: true,
      strokeColor: '#224049',
      strokeOpacity: 0.8,
      strokeWeight: 4,
      map: map
    });

    return () => {
      polyline.setMap(null);
    };
  }, [map, path]);

  return null;
};

// Component to decode and render encoded polyline from Google Directions
const EncodedPolyline = ({ encodedPath, map }) => {
  useEffect(() => {
    if (!map || !encodedPath || !window.google?.maps?.geometry) return;

    try {
      const decodedPath = window.google.maps.geometry.encoding.decodePath(encodedPath);
      
      const polyline = new window.google.maps.Polyline({
        path: decodedPath,
        geodesic: true,
        strokeColor: '#224049',
        strokeOpacity: 0.8,
        strokeWeight: 4,
        map: map
      });

      return () => {
        polyline.setMap(null);
      };
    } catch (error) {
      console.error('Error decoding polyline:', error);
    }
  }, [map, encodedPath]);

  return null;
};

// Rider location marker with custom styling
const RiderMarker = ({ position }) => {
  return (
    <AdvancedMarker position={position}>
      <div className="relative">
        {/* Pulsing ring animation */}
        <div className="absolute -inset-3 bg-green-400 rounded-full opacity-30 animate-ping" />
        {/* Main marker */}
        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </div>
      </div>
    </AdvancedMarker>
  );
};

export const DeliveryMap = ({
  stops = [],
  currentLocation = null,
  polylinePath = [],
  encodedPolyline = null,
  showRouteHistory = false,
  routeHistory = [],
  onMarkerClick = () => {},
  centerOnRider = false
}) => {
  const map = useMap();
  const [selectedMarker, setSelectedMarker] = useState(null);

  // Calculate map bounds to fit all markers
  useEffect(() => {
    if (!map) return;

    try {
      const bounds = new window.google.maps.LatLngBounds();
      let hasPoints = false;
      
      stops.forEach(stop => {
        if (stop.latitude && stop.longitude) {
          bounds.extend({ lat: stop.latitude, lng: stop.longitude });
          hasPoints = true;
        }
      });

      if (currentLocation?.latitude && currentLocation?.longitude) {
        bounds.extend({
          lat: currentLocation.latitude,
          lng: currentLocation.longitude
        });
        hasPoints = true;
      }

      if (hasPoints) {
        map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      }
    } catch (error) {
      console.error('Error fitting bounds:', error);
    }
  }, [map, stops, currentLocation]);

  // Center on rider when tracking
  useEffect(() => {
    if (centerOnRider && currentLocation && map) {
      map.panTo({
        lat: currentLocation.latitude,
        lng: currentLocation.longitude
      });
    }
  }, [centerOnRider, currentLocation, map]);

  const getMarkerColor = useCallback((status) => {
    switch (status) {
      case 'completed': return '#34C759';
      case 'in_progress': return '#FF9500';
      case 'pending': return '#007AFF';
      default: return '#007AFF';
    }
  }, []);

  const handleMarkerClick = useCallback((stopId) => {
    setSelectedMarker(stopId);
    onMarkerClick(stopId);
  }, [onMarkerClick]);

  return (
    <Map 
      defaultZoom={12}
      defaultCenter={{ lat: 20.6597, lng: -103.3496 }}
      mapTypeId="roadmap"
      gestureHandling="greedy"
      fullscreenControl={true}
      style={{ width: '100%', height: '100%' }}
    >
      {/* Render encoded polyline from Google Directions */}
      {encodedPolyline && (
        <EncodedPolyline encodedPath={encodedPolyline} map={map} />
      )}

      {/* Render custom polyline path */}
      {polylinePath && polylinePath.length > 1 && (
        <RoutePolyline path={polylinePath} map={map} />
      )}

      {/* Render route history trail */}
      {showRouteHistory && routeHistory.length > 1 && (
        <RoutePolyline path={routeHistory} map={map} />
      )}

      {/* Render current location marker (rider) */}
      {currentLocation && currentLocation.latitude && currentLocation.longitude && (
        <RiderMarker 
          position={{
            lat: currentLocation.latitude,
            lng: currentLocation.longitude
          }}
        />
      )}

      {/* Render delivery stop markers */}
      {stops.map((stop, index) => (
        <AdvancedMarker
          key={stop.id}
          position={{ lat: stop.latitude, lng: stop.longitude }}
          onClick={() => handleMarkerClick(stop.id)}
        >
          <Pin 
            background={getMarkerColor(stop.status)} 
            glyphColor={'#fff'}
            borderColor={'#000'}
            glyph={`${index + 1}`}
          />
          {selectedMarker === stop.id && (
            <InfoWindow
              position={{ lat: stop.latitude, lng: stop.longitude }}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div style={{ padding: '8px', fontSize: '12px', minWidth: '150px' }}>
                <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', color: '#224049' }}>
                  Parada {index + 1}
                </p>
                <p style={{ margin: '0 0 4px 0' }}>{stop.address}</p>
                {stop.customer_name && (
                  <p style={{ margin: '0 0 4px 0', fontWeight: '500' }}>
                    Cliente: {stop.customer_name}
                  </p>
                )}
                <p style={{ margin: '0', color: '#666' }}>
                  Estado: {stop.status === 'pending' ? 'Pendiente' : stop.status === 'in_progress' ? 'En progreso' : 'Completado'}
                </p>
                {stop.eta_minutes && (
                  <p style={{ margin: '4px 0 0 0', color: '#C38154', fontWeight: '500' }}>
                    ETA: {stop.eta_minutes} min
                  </p>
                )}
              </div>
            </InfoWindow>
          )}
        </AdvancedMarker>
      ))}
    </Map>
  );
};
